// 오라클 전문가 창의 공통 틀 — **DB 연결마다 탭**(2026-10-14 요청: 여러 DB 를 동시에 조회).
//
// 오라클 성능 분석 · 세션 분석 · 락 대기 · 자원 분석 · DBMS 카탈로그가 이 틀을 쓴다. 탭마다 그 창의 본문(renderBody)이 하나씩이고,
// 본문은 각자 useOwnConnSession 으로 **자기 연결의 전담 세션**을 갖는다. 다른 탭으로 옮겨도 마운트된 채 숨기므로 조회 결과·조건이 남는다.
// 같은 연결은 탭 하나만 — 전담 세션은 (창, 연결)마다 하나라 두 탭이 같은 연결을 열면 한 세션에 문장이 섞인다.
// 「DB 연결 추가」 와 탭 안의 연결 고르기에는 다른 탭이 연 연결이 나오지 않는다(excludeConnIds).
import React, { useState, useEffect, useMemo, useCallback, useContext, createContext } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useStore } from 'react-redux';
import { FiDatabase, FiX } from 'react-icons/fi';
import { closeSession } from '../../redux/actions/sessionActions';
import AppModal from '../common/AppModal';
import './ConnTabsWindow.css';

let tabSeq = 0;

// 탭 줄 오른쪽 자리 — 탭 본문이 자기 세션 표시를 여기에 그린다(연결은 탭 이름이 이미 말하므로 줄을 따로 두지 않는다)
const SlotContext = createContext(null);

export default function ConnTabsWindow({
    isOpen, onClose, connections = [], defaultConnectionId, filter, renderBody, sessionLabel, focus, ...modalProps
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const eligible = useMemo(() => connections.filter(c => c.id && (!filter || filter(c))), [connections, filter]);
    const [tabs, setTabs] = useState([]);          // [{ key, connId }]
    const [activeKey, setActiveKey] = useState(null);
    const [slot, setSlot] = useState(null);

    // 열 때 — 기본 연결 하나로 시작(본문의 useOwnConnSession 이 기본 연결을 정한다: 지금 쓰는 연결, 없으면 목록 첫 번째)
    useEffect(() => {
        if (!isOpen) { setTabs([]); setActiveKey(null); return; }
        if (tabs.length > 0) return;
        const key = `ct${++tabSeq}`;
        // 다른 창이 이 창을 특정 연결로 열었으면(focus — utils/perfJump) 그 연결로 시작한다
        setTabs([{ key, connId: (focus && focus.connId) || defaultConnectionId || null }]);
        setActiveKey(key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // 이미 열린 창에 「이 연결로」 가 오면 — 그 연결의 탭을 앞으로, 없으면 연다(2026-10-22, 세션 분석 → 실행 이력)
    const focusSeq = focus ? focus.seq : 0;
    useEffect(() => {
        if (!isOpen || !focus || !focus.connId) return;
        setTabs(prev => {
            if (prev.length === 0) return prev;                // 위의 여는 효과가 그 연결로 시작한다
            const exist = prev.find(x => x.connId === focus.connId);
            if (exist) { setActiveKey(exist.key); return prev; }
            const key = `ct${++tabSeq}`;
            setActiveKey(key);
            return [...prev, { key, connId: focus.connId }];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusSeq]);

    const openIds = tabs.map(x => x.connId).filter(Boolean);
    const addable = eligible.filter(c => !openIds.includes(c.id));
    const addTab = (connId) => {
        if (!connId) return;
        const exist = tabs.find(x => x.connId === connId);
        if (exist) { setActiveKey(exist.key); return; }
        const key = `ct${++tabSeq}`;
        setTabs([...tabs, { key, connId }]);
        setActiveKey(key);
    };
    // 탭을 닫으면 그 탭의 전담 세션도 닫는다(2026-10-14 요청). 창을 닫을 때는 그대로 둔다 — 다시 열 때 쓴다(useOwnConnSession 2번).
    // 고른 것은 이 창 키(sessionLabel)·그 연결의 전담 세션뿐이다 — 탭에서 빌려 쓴 편집기 세션은 건드리지 않는다.
    const closeTabSessions = (connId) => {
        if (!sessionLabel || !connId) return;
        const list = store.getState().sessions?.list || [];
        list.filter(x => x && !x.ephemeral && x.category === 'monitor' && x.label === sessionLabel && x.connectionId === connId)
            .forEach(x => { dispatch(closeSession(x.sessionId)).catch(() => {}); });
    };
    const closeTab = (key) => {
        const i = tabs.findIndex(x => x.key === key);
        closeTabSessions(tabs[i] && tabs[i].connId);
        const next = tabs.filter(x => x.key !== key);
        setTabs(next);
        // 마지막 탭을 닫아도 창은 남긴다 — 빈 자리에서 「DB 연결 추가」 로 다시 연다(2026-10-14 요청)
        if (key === activeKey) setActiveKey(next.length ? next[Math.max(0, i - 1)].key : null);
    };
    // 탭 안에서 연결을 바꾸면 탭 이름이 따라간다
    const setTabConn = useCallback((key, connId) => {
        setTabs(prev => prev.map(x => (x.key === key && x.connId !== connId ? { ...x, connId } : x)));
    }, []);
    const nameOf = (id) => {
        const c = connections.find(x => x.id === id);
        return c ? (c.name || c.id) : t('monitoring:ct.connPending');
    };

    if (!isOpen) return null;
    return (
        // 연결 탭은 **제목 바로 오른쪽**에(2026-10-22 요청 — 예전에는 제목 아래 줄 하나를 따로 썼다). 탭 본문의 세션 표시(slot)도 함께 올라간다
        <AppModal windowed open onClose={onClose} {...modalProps} titleExtra={
            <div className="conntabs conntabs-inheader" role="tablist" aria-label={t('monitoring:ct.connTabs')}>
                {tabs.map(tab => (
                    <div key={tab.key} className={`conntabs-tab ${tab.key === activeKey ? 'active' : ''}`}>
                        <button type="button" role="tab" aria-selected={tab.key === activeKey} className="conntabs-main" onClick={() => setActiveKey(tab.key)}>
                            <FiDatabase size={12} /> {nameOf(tab.connId)}
                        </button>
                        {/* 탭이 하나여도 닫을 수 있다(Oracle 연결이 하나면 늘 한 탭이라 닫기가 안 보였다) */}
                        {(
                            <button type="button" className="conntabs-x" onClick={() => closeTab(tab.key)}
                                aria-label={t('monitoring:ct.closeConnTab', { name: nameOf(tab.connId) })}
                                title={t('monitoring:ct.closeConnTab', { name: nameOf(tab.connId) })}>
                                <FiX size={11} />
                            </button>
                        )}
                    </div>
                ))}
                {addable.length > 0 && (
                    <select className="conntabs-add" value="" onChange={(e) => addTab(e.target.value)}
                        aria-label={t('monitoring:ct.addConnTab')} title={t('monitoring:ct.addConnTabHint')}>
                        <option value="">{t('monitoring:ct.addConnTab')}</option>
                        {addable.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                    </select>
                )}
                <div className="conntabs-slot" ref={setSlot} />
            </div>
        }>
            <SlotContext.Provider value={slot}>
            {tabs.map(tab => (
                <React.Fragment key={tab.key}>
                    {renderBody({
                        defaultConnectionId: tab.connId,
                        active: tab.key === activeKey,
                        excludeConnIds: openIds.filter(id => id !== tab.connId),
                        onConnectionChange: (id) => setTabConn(tab.key, id),
                    })}
                </React.Fragment>
            ))}
            {tabs.length === 0 && (
                <div className="conntabs-empty">
                    <FiDatabase size={20} />
                    <span>{t('monitoring:ct.noTabs')}</span>
                </div>
            )}
            </SlotContext.Provider>
        </AppModal>
    );
}

ConnTabsWindow.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    connections: PropTypes.array,
    defaultConnectionId: PropTypes.string,
    filter: PropTypes.func,
    renderBody: PropTypes.func.isRequired,
    /** 본문의 전담 세션 라벨(창 키) — 탭을 닫을 때 그 세션을 닫는다 */
    sessionLabel: PropTypes.string,
    /** 다른 창이 고른 연결 { connId, seq } — 그 연결의 탭을 앞으로(utils/perfJump) */
    focus: PropTypes.shape({ connId: PropTypes.string, seq: PropTypes.number }),
};

/**
 * 탭 본문의 겉 — 숨길 때는 display:none(마운트 유지). 그 탭의 세션 표시(toolbar)는 탭 줄 오른쪽 자리에 그린다
 * (2026-10-14 — 연결은 탭 이름과 겹쳐서 줄 하나를 따로 두지 않는다). 창 밖에서 쓰면 본문 맨 위에 그린다.
 */
export function ConnTabPane({ active = true, toolbar, children }) {
    const slot = useContext(SlotContext);
    const bar = toolbar && <div className="conntabs-pane-toolbar" style={active ? undefined : { display: 'none' }}>{toolbar}</div>;
    return (
        <div className="conntabs-pane" style={active ? undefined : { display: 'none' }} role="tabpanel">
            {bar && (slot ? createPortal(bar, slot) : bar)}
            {children}
        </div>
    );
}
ConnTabPane.propTypes = { active: PropTypes.bool, toolbar: PropTypes.node, children: PropTypes.node };
